import { useState, useCallback, useRef, useEffect } from "react";

type JState = "none" | "tender" | "swollen" | "both";
type Mode   = "tender" | "swollen";

function nextState(cur: JState, mode: Mode): JState {
  if (mode === "tender") {
    return cur === "none" ? "tender" : cur === "tender" ? "none" : cur === "swollen" ? "both" : "swollen";
  }
  return cur === "none" ? "swollen" : cur === "swollen" ? "none" : cur === "tender" ? "both" : "tender";
}
function jFill(s: JState)   { return s === "tender" ? "#fb923c" : s === "swollen" ? "#60a5fa" : s === "both" ? "#a78bfa" : "#f0f7ff"; }
function jStroke(s: JState) { return s === "tender" ? "#ea580c" : s === "swollen" ? "#2563eb" : s === "both" ? "#7c3aed" : "#93c5fd"; }
function jGlow(s: JState)   { return s === "tender" ? "rgba(251,146,60,.7)" : s === "swollen" ? "rgba(96,165,250,.7)" : s === "both" ? "rgba(167,139,250,.7)" : "rgba(147,197,253,.55)"; }
function jLabel(s: JState)  { return s === "tender" ? "T" : s === "swollen" ? "S" : s === "both" ? "B" : ""; }

// ── Canvas dimensions ─────────────────────────────────────────────────────────
const CW = 1100, CH = 800;
// 9:16 body image positioned in canvas
const IX = 377, IY = 44, IW = 345, IH = 613;

// ── Joint positions ───────────────────────────────────────────────────────────
const BODY = {
  LS: { x: 430, y: 192 }, RS: { x: 667, y: 192 },
  LE: { x: 401, y: 301 }, RE: { x: 699, y: 301 },
  LW: { x: 385, y: 380 }, RW: { x: 715, y: 380 },
  LF: { x: 416, y: 454 }, RF: { x: 684, y: 454 },
  LK: { x: 487, y: 521 }, RK: { x: 613, y: 521 },
};

// ── Panel geometry ────────────────────────────────────────────────────────────
const LP = {
  SH: { bx: 167, by: 165, bw: 160, bh: 50  },
  EL: { bx: 177, by: 275, bw: 150, bh: 50  },
  WH: { bx: 117, by: 344, bw: 210, bh: 116 },
  KN: { bx: 179, by: 496, bw: 148, bh: 50  },
};
const RP = {
  SH: { bx: 772, by: 165, bw: 160, bh: 50  },
  EL: { bx: 772, by: 275, bw: 150, bh: 50  },
  WH: { bx: 768, by: 344, bw: 210, bh: 116 },
  KN: { bx: 772, by: 496, bw: 148, bh: 50  },
};

function lc(p: { bx: number; bw: number }, anchorY: number) { return { px: p.bx + p.bw, py: anchorY }; }
function rc(p: { bx: number },              anchorY: number) { return { px: p.bx,         py: anchorY }; }

// ── Sub-components (module-scope to avoid React anti-pattern of redefining
// components on every render, which can trigger "Invalid hook call" and
// "Maximum update depth exceeded" errors) ────────────────────────────────────

function BodyDot({ cx, cy, st, r = 6 }: { cx: number; cy: number; st: JState; r?: number }) {
  return (
    <g style={{ pointerEvents: "none" }}>
      <circle cx={cx} cy={cy} r={r + 5} fill="none" stroke={jGlow(st)} strokeWidth="3" opacity=".45" />
      <circle cx={cx} cy={cy} r={r}     fill={jFill(st)} stroke={jStroke(st)} strokeWidth="1.5" filter="url(#gl)" />
    </g>
  );
}

function CDot({ id, st, onToggle, size = 24 }: { id: string; st: JState; onToggle: (id: string) => void; size?: number }) {
  return (
    <button
      onClick={() => onToggle(id)}
      title={id.replace(/_/g, " ")}
      style={{
        width: size, height: size, borderRadius: "50%",
        background: jFill(st), border: `2px solid ${jStroke(st)}`,
        boxShadow: `0 0 8px ${jGlow(st)}`,
        color: st === "none" ? "#6b9fd8" : "white",
        fontSize: size < 18 ? 7 : 9, fontWeight: 800,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", flexShrink: 0, padding: 0,
        transition: "transform .1s",
      }}
    >
      {jLabel(st)}
    </button>
  );
}

function Panel({
  title, x, y, w, h, arrowY, side, children,
}: {
  title: string; x: number; y: number; w: number; h: number;
  arrowY: number; side: "left" | "right"; children: React.ReactNode;
}) {
  const arrowOffset = Math.max(8, Math.min(h - 16, arrowY - y - 7));
  return (
    <div style={{
      position: "absolute", left: x, top: y, width: w, height: h,
      background: "rgba(255,255,255,0.96)",
      border: "1.5px solid rgba(147,197,253,0.65)",
      borderRadius: 10,
      boxShadow: "0 3px 16px rgba(59,130,246,.12), 0 1px 3px rgba(0,0,0,.05)",
      overflow: "visible",
    }}>
      <div style={{
        position: "absolute",
        [side === "left" ? "right" : "left"]: -9,
        top: arrowOffset, width: 0, height: 0,
        borderTop: "7px solid transparent",
        borderBottom: "7px solid transparent",
        [side === "left" ? "borderLeft" : "borderRight"]: "9px solid rgba(147,197,253,0.75)",
      }} />
      <div style={{
        background: "linear-gradient(90deg,#dbeafe,#eff6ff)",
        padding: "4px 10px", fontSize: 10, fontWeight: 700, color: "#1e40af",
        letterSpacing: ".04em",
        borderBottom: "1px solid rgba(147,197,253,.35)",
        borderRadius: "8px 8px 0 0",
      }}>{title}</div>
      <div style={{ padding: "8px 10px", height: "calc(100% - 28px)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        {children}
      </div>
    </div>
  );
}

function Conn({ px, py, bx, by, side }: { px: number; py: number; bx: number; by: number; side: "left" | "right" }) {
  const pull = Math.min(55, Math.abs(bx - px) * 0.38);
  const cp1x = px + (side === "left"  ?  pull : -pull);
  const cp2x = bx + (side === "left"  ? -pull :  pull);
  return (
    <path
      d={`M${px},${py} C${cp1x},${py} ${cp2x},${by} ${bx},${by}`}
      fill="none" stroke="rgba(147,197,253,.7)" strokeWidth="1.3" strokeDasharray="5,3"
    />
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────
export interface JointSelectorProps {
  onChange: (tjc: number, sjc: number) => void;
  label?: string;
}

export function JointSelector({ onChange, label }: JointSelectorProps) {
  const [mode,   setMode]   = useState<Mode>("tender");
  const [joints, setJoints] = useState<Record<string, JState>>({});
  const [scale,  setScale]  = useState(1);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Responsive scale — shrink to fit container, never enlarge
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setScale(Math.min(1, w / CW));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const toggle = useCallback((id: string) => {
    setJoints((prev) => {
      const next = { ...prev, [id]: nextState(prev[id] || "none", mode) };
      const tjc = Object.values(next).filter(v => v === "tender" || v === "both").length;
      const sjc = Object.values(next).filter(v => v === "swollen" || v === "both").length;
      onChange(tjc, sjc);
      return next;
    });
  }, [mode, onChange]);

  const reset = () => { setJoints({}); onChange(0, 0); };
  const s = (id: string): JState => joints[id] || "none";
  const tjc = Object.values(joints).filter(v => v === "tender"  || v === "both").length;
  const sjc = Object.values(joints).filter(v => v === "swollen" || v === "both").length;

  // Connector anchors
  const LSH_A = lc(LP.SH, BODY.LS.y);
  const LEL_A = lc(LP.EL, BODY.LE.y);
  const LWR_A = lc(LP.WH, BODY.LW.y);
  const LKN_A = lc(LP.KN, BODY.LK.y);
  const RSH_A = rc(RP.SH, BODY.RS.y);
  const REL_A = rc(RP.EL, BODY.RE.y);
  const RWR_A = rc(RP.WH, BODY.RW.y);
  const RKN_A = rc(RP.KN, BODY.RK.y);

  return (
    <div ref={wrapRef} style={{ width: "100%" }}>
      {/* Scaled canvas wrapper */}
      <div style={{
        width: CW,
        height: CH + 72,
        transformOrigin: "top left",
        transform: `scale(${scale})`,
        marginBottom: scale < 1 ? (CH + 72) * (scale - 1) : 0,
      }}>
        <div style={{
          width: CW, height: CH + 72,
          background: "#f5f9ff",
          fontFamily: "system-ui,-apple-system,sans-serif",
          position: "relative", overflow: "hidden",
        }}>

          {/* Top bar */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 42,
            display: "flex", alignItems: "center", justifyContent: "center",
            borderBottom: "1px solid rgba(147,197,253,.2)",
            background: "rgba(255,255,255,.7)", zIndex: 20,
          }}>
            <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: ".1em", color: "#1e3a5f", textTransform: "uppercase" }}>
              {label ?? "DAS28 Joint Selector"}
            </span>
            <div style={{ position: "absolute", right: 14, display: "flex", gap: 8 }}>
              <div style={{ display: "flex", borderRadius: 7, overflow: "hidden", border: "1px solid rgba(147,197,253,.6)" }}>
                {(["tender", "swollen"] as Mode[]).map(m => (
                  <button key={m} onClick={() => setMode(m)} style={{
                    padding: "4px 14px", fontSize: 11, fontWeight: 700,
                    background: mode === m ? (m === "tender" ? "#fb923c" : "#3b82f6") : "white",
                    color: mode === m ? "white" : "#64748b",
                    border: "none", cursor: "pointer",
                  }}>
                    {m === "tender" ? "Tender" : "Swollen"}
                  </button>
                ))}
              </div>
              <button onClick={reset} style={{
                padding: "4px 10px", fontSize: 11, background: "white",
                border: "1px solid rgba(147,197,253,.6)", borderRadius: 6,
                cursor: "pointer", color: "#64748b", fontWeight: 600,
              }}>Reset</button>
            </div>
          </div>

          {/* Body image */}
          <img
            src="/body-medical-nobg.png"
            alt="Anatomical body diagram"
            style={{
              position: "absolute", left: IX, top: IY, width: IW, height: IH,
              objectFit: "contain", pointerEvents: "none", userSelect: "none",
              opacity: .92,
            }}
          />

          {/* SVG layer — decorative only, must not intercept clicks */}
          <svg viewBox={`0 0 ${CW} ${CH}`} width={CW} height={CH}
            style={{ position: "absolute", left: 0, top: 0, zIndex: 5, pointerEvents: "none" }}>
            <defs>
              <filter id="jsel-gl" x="-80%" y="-80%" width="260%" height="260%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="b"/>
                <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            </defs>

            {/* Connectors — left */}
            <Conn px={LSH_A.px} py={LSH_A.py} bx={BODY.LS.x} by={BODY.LS.y} side="left" />
            <Conn px={LEL_A.px} py={LEL_A.py} bx={BODY.LE.x} by={BODY.LE.y} side="left" />
            <Conn px={LWR_A.px} py={LWR_A.py} bx={BODY.LW.x} by={BODY.LW.y} side="left" />
            <Conn px={LKN_A.px} py={LKN_A.py} bx={BODY.LK.x} by={BODY.LK.y} side="left" />

            {/* Connectors — right */}
            <Conn px={RSH_A.px} py={RSH_A.py} bx={BODY.RS.x} by={BODY.RS.y} side="right" />
            <Conn px={REL_A.px} py={REL_A.py} bx={BODY.RE.x} by={BODY.RE.y} side="right" />
            <Conn px={RWR_A.px} py={RWR_A.py} bx={BODY.RW.x} by={BODY.RW.y} side="right" />
            <Conn px={RKN_A.px} py={RKN_A.py} bx={BODY.RK.x} by={BODY.RK.y} side="right" />

            {/* Body joint dots */}
            {([
              ["L_Shoulder", BODY.LS], ["R_Shoulder", BODY.RS],
              ["L_Elbow",    BODY.LE], ["R_Elbow",    BODY.RE],
              ["L_Wrist",    BODY.LW], ["R_Wrist",    BODY.RW],
              ["L_Knee",     BODY.LK], ["R_Knee",     BODY.RK],
            ] as [string, { x: number; y: number }][]).map(([id, { x, y }]) => (
              <BodyDot key={id} st={s(id)} cx={x} cy={y} r={7} />
            ))}

            {/* Small endpoint circles */}
            {[BODY.LS, BODY.RS, BODY.LE, BODY.RE, BODY.LW, BODY.RW, BODY.LK, BODY.RK].map(({ x, y }, i) => (
              <circle key={i} cx={x} cy={y} r={3} fill="#93c5fd" opacity=".6" />
            ))}
          </svg>

          {/* LEFT PANELS */}
          <Panel title="Left Shoulder" x={LP.SH.bx} y={LP.SH.by} w={LP.SH.bw} h={LP.SH.bh} arrowY={BODY.LS.y} side="left">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CDot id="L_Shoulder" st={s("L_Shoulder")} onToggle={toggle} size={26} />
              <span style={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.3 }}>Tap to mark<br />tender or swollen</span>
            </div>
          </Panel>

          <Panel title="Left Elbow" x={LP.EL.bx} y={LP.EL.by} w={LP.EL.bw} h={LP.EL.bh} arrowY={BODY.LE.y} side="left">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CDot id="L_Elbow" st={s("L_Elbow")} onToggle={toggle} size={26} />
              <span style={{ fontSize: 10, color: "#94a3b8" }}>Tap to mark</span>
            </div>
          </Panel>

          <Panel title="Left Wrist &amp; Hand" x={LP.WH.bx} y={LP.WH.by} w={LP.WH.bw} h={LP.WH.bh} arrowY={BODY.LW.y} side="left">
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 7, marginBottom: 7, borderBottom: "1px solid rgba(147,197,253,.25)" }}>
                <span style={{ fontSize: 9, color: "#64748b", fontWeight: 600, width: 32 }}>Wrist</span>
                <CDot id="L_Wrist" st={s("L_Wrist")} onToggle={toggle} size={22} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "#64748b", width: 28 }}>MCP</span>
                {[1,2,3,4,5].map(i => <CDot key={i} st={s(`L_MCP${i}`)} onToggle={toggle} id={`L_MCP${i}`} size={18} />)}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "#64748b", width: 28 }}>PIP</span>
                {[1,2,3,4,5].map(i => <CDot key={i} st={s(`L_PIP${i}`)} onToggle={toggle} id={`L_PIP${i}`} size={18} />)}
              </div>
              <div style={{ display: "flex", gap: 5, paddingLeft: 33 }}>
                {[1,2,3,4,5].map(i => (
                  <span key={i} style={{ width: 18, textAlign: "center", fontSize: 7, color: "#cbd5e1" }}>{i}</span>
                ))}
              </div>
            </div>
          </Panel>

          <Panel title="Left Knee" x={LP.KN.bx} y={LP.KN.by} w={LP.KN.bw} h={LP.KN.bh} arrowY={BODY.LK.y} side="left">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CDot id="L_Knee" st={s("L_Knee")} onToggle={toggle} size={26} />
              <span style={{ fontSize: 10, color: "#94a3b8" }}>Tap to mark</span>
            </div>
          </Panel>

          {/* RIGHT PANELS */}
          <Panel title="Right Shoulder" x={RP.SH.bx} y={RP.SH.by} w={RP.SH.bw} h={RP.SH.bh} arrowY={BODY.RS.y} side="right">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CDot id="R_Shoulder" st={s("R_Shoulder")} onToggle={toggle} size={26} />
              <span style={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.3 }}>Tap to mark<br />tender or swollen</span>
            </div>
          </Panel>

          <Panel title="Right Elbow" x={RP.EL.bx} y={RP.EL.by} w={RP.EL.bw} h={RP.EL.bh} arrowY={BODY.RE.y} side="right">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CDot id="R_Elbow" st={s("R_Elbow")} onToggle={toggle} size={26} />
              <span style={{ fontSize: 10, color: "#94a3b8" }}>Tap to mark</span>
            </div>
          </Panel>

          <Panel title="Right Wrist &amp; Hand" x={RP.WH.bx} y={RP.WH.by} w={RP.WH.bw} h={RP.WH.bh} arrowY={BODY.RW.y} side="right">
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 7, marginBottom: 7, borderBottom: "1px solid rgba(147,197,253,.25)" }}>
                <span style={{ fontSize: 9, color: "#64748b", fontWeight: 600, width: 32 }}>Wrist</span>
                <CDot id="R_Wrist" st={s("R_Wrist")} onToggle={toggle} size={22} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "#64748b", width: 28 }}>MCP</span>
                {[1,2,3,4,5].map(i => <CDot key={i} st={s(`R_MCP${i}`)} onToggle={toggle} id={`R_MCP${i}`} size={18} />)}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "#64748b", width: 28 }}>PIP</span>
                {[1,2,3,4,5].map(i => <CDot key={i} st={s(`R_PIP${i}`)} onToggle={toggle} id={`R_PIP${i}`} size={18} />)}
              </div>
              <div style={{ display: "flex", gap: 5, paddingLeft: 33 }}>
                {[1,2,3,4,5].map(i => (
                  <span key={i} style={{ width: 18, textAlign: "center", fontSize: 7, color: "#cbd5e1" }}>{i}</span>
                ))}
              </div>
            </div>
          </Panel>

          <Panel title="Right Knee" x={RP.KN.bx} y={RP.KN.by} w={RP.KN.bw} h={RP.KN.bh} arrowY={BODY.RK.y} side="right">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CDot id="R_Knee" st={s("R_Knee")} onToggle={toggle} size={26} />
              <span style={{ fontSize: 10, color: "#94a3b8" }}>Tap to mark</span>
            </div>
          </Panel>

          {/* Score strip */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0, height: 72,
            background: "rgba(255,255,255,.88)",
            borderTop: "1px solid rgba(147,197,253,.3)",
            backdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 28, zIndex: 20,
          }}>
            {[
              { id: "tjc", val: tjc, bg: "#fb923c", glow: "rgba(251,146,60,.5)", label: "Tender (TJC)", tc: "#9a3412" },
              { id: "sjc", val: sjc, bg: "#3b82f6", glow: "rgba(59,130,246,.5)", label: "Swollen (SJC)", tc: "#1e40af" },
            ].map(({ id, val, bg, glow, label: lbl, tc }) => (
              <div key={id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: "50%", background: bg,
                  boxShadow: `0 0 12px ${glow}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 16, fontWeight: 900, color: "white",
                }}>{val}</div>
                <div style={{ lineHeight: 1.3 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: tc }}>{lbl}</div>
                  <div style={{ fontSize: 10, color: bg }}>/ 28</div>
                </div>
              </div>
            ))}
            <div style={{ width: 1, height: 36, background: "rgba(147,197,253,.3)" }} />
            <div style={{ display: "flex", gap: 14, fontSize: 10, color: "#64748b" }}>
              {[["#fb923c", "Tender (T)"], ["#60a5fa", "Swollen (S)"], ["#a78bfa", "Both (B)"]].map(([bg, lbl]) => (
                <span key={lbl} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: bg, display: "inline-block" }} />
                  {lbl}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
